/**
 * Mini Map Card Plugin for Search Results Map Variant
 *
 * Renders a tooltip card next to a hovered marker on desktop. The card is
 * appended to a dedicated overlay element that sits outside the marker DOM
 * tree, so card clicks never bubble to the marker's own click handler.
 *
 * Single responsibility: show and dismiss the desktop hover tooltip.
 *
 * This plugin does NOT:
 * - Run on mobile (see carousel.plugin)
 * - Handle marker clicks or URL navigation (see markers.plugin)
 * - Manage the marker layer or map canvas (see map.plugin, markers.plugin)
 * - Render the mobile bottom carousel (see carousel.plugin)
 *
 * Reason to change: tooltip positioning logic, card overlay lifecycle,
 * hover/dismiss behaviour, or desktop card rendering.
 *
 * @module searchResultsMapVariant/plugins/miniMapCard.plugin
 */

import {
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  map,
  of,
  switchMap,
  throttleTime,
  timer,
} from 'rxjs';
import {
  selectMapResults,
} from '../redux/selectors';
import { mapMarkerToCardInformation } from '../services/mapper';
import { resolveTooltipDirection } from '../services/cardBuilder';
import {
  createCardOverlay,
  removeCardElements,
  buildCardElement,
  mountCardAtGeoPosition,
} from '../components/miniMapCardDom';

const HOVER_DELAY_MS = 500;
const MOUSEOVER_THROTTLE_MS = 50;

export default function createMiniMapCardPlugin() {
  const pluginState = {
    started: false,
    mapFacade: null,
    mapContainer: null,
    cardOverlay: null,
    isDesktopBreakpoint: null,
    hoveredMarker: null,
    subscriptions: [],
  };

  function removeMiniMapCard() {
    removeCardElements(pluginState.cardOverlay);
  }

  function buildCard(cardData) {
    return buildCardElement(cardData, (event) => {
      if (!event.relatedTarget?.closest?.('[data-marker-icon]')) {
        pluginState.hoveredMarker = null;
        removeMiniMapCard();
      }
    });
  }

  function renderMapCard(cards, { position }) {
    if (!cards?.length || !pluginState.mapContainer || !pluginState.cardOverlay) {
      return;
    }

    removeMiniMapCard();

    const cardElement = buildCard(cards[0]);
    if (!cardElement) {
      return;
    }

    const bounds = pluginState.mapFacade?.getBounds?.();
    if (!bounds) {
      return;
    }

    const direction = resolveTooltipDirection(bounds, position);
    mountCardAtGeoPosition(cardElement, position, bounds, pluginState.mapContainer, pluginState.cardOverlay, direction);
  }

  /**
   * Resolves card data synchronously and pushes it into Redux state.
   *
   * @param {Object} selectedMarker - The hovered marker object.
   */
  function handleMarkerSelection(selectedMarker) {
    const metadata = { ...mapMarkerToCardInformation(selectedMarker), type: selectedMarker.type };
    pluginState.hoverCards = [metadata];
  }

  function setupSubscriptions() {
    // Desktop: react to hover via event delegation on the map container.
    pluginState.subscriptions.push(
      fromEvent(pluginState.mapContainer, 'mouseover').pipe(
        throttleTime(MOUSEOVER_THROTTLE_MS, undefined, { leading: true, trailing: true }),
        filter(() => pluginState.isDesktopBreakpoint?.() ?? false),
        map((event) => {
          const markerEl = event.target.closest('[data-marker-icon]');
          if (markerEl) {
            return markerEl.dataset.id ?? null;
          }

          // Mouse is on the card overlay — treat as "still hovering".
          if (event.target.closest('[data-mini-map-card]')) {
            return 'card';
          }
          return null;
        }),
        distinctUntilChanged(),
        switchMap((id) => {
          if (id === 'card') {
            return EMPTY;
          }

          if (!id) {
            return pluginState.hoveredMarker ? of(null) : EMPTY;
          }

          return timer(HOVER_DELAY_MS).pipe(
            map(() => selectMapResults(pluginState.getState())?.find((m) => String(m.id) === id) ?? null),
          );
        }),
      ).subscribe((hoveredMarker) => {
        pluginState.hoveredMarker = hoveredMarker;
        if (!hoveredMarker) {
          removeMiniMapCard();
          pluginState.hoverCards = [];
          return;
        }
        handleMarkerSelection(hoveredMarker);
        renderMapCard(pluginState.hoverCards, pluginState.hoveredMarker);
      }),
    );
  }

  function clearSubscriptions() {
    pluginState.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    pluginState.subscriptions = [];
  }

  return {
    name: 'mini-map-card',

    async init(context) {
      context.state.miniMapCard = { started: false };
    },

    async start(context) {
      if (pluginState.started) {
        return;
      }

      pluginState.mapFacade = context.services.mapFacade;
      pluginState.mapContainer = context.root;
      pluginState.getState = context.services.getState;
      pluginState.dispatch = context.services.dispatch;
      pluginState.store$ = context.services.store$;
      pluginState.isDesktopBreakpoint = context.services.isDesktopBreakpoint;

      if (!pluginState.mapContainer) {
        return;
      }

      pluginState.cardOverlay = createCardOverlay(pluginState.mapContainer);

      setupSubscriptions();

      pluginState.started = true;
      context.state.miniMapCard = { started: true };
    },

    async stop(context) {
      clearSubscriptions();
      removeMiniMapCard();

      pluginState.cardOverlay?.remove();
      pluginState.cardOverlay = null;

      pluginState.mapFacade = null;
      pluginState.mapContainer = null;
      pluginState.hoveredMarker = null;
      pluginState.isDesktopBreakpoint = null;
      pluginState.getState = null;
      pluginState.dispatch = null;
      pluginState.started = false;

      delete context.state.miniMapCard;
    },
  };
}
